import { z } from "zod";

import { DbEnum } from "../../generated/prisma-enums";
import { PlatformListenerIdleConfigSchema } from "../../shared/domain/tools/platform-listener-idle.schema";

/**
 * Customer-facing prompt shape for the "ask the customer for a rating"
 * mode. The discriminator gates which channel surfaces accept the value:
 *
 *   - `freeText`         — plain message body. Used by Slack, Web, Email,
 *                          and any non-WhatsApp deployment. The materialiser
 *                          renders this verbatim into a `SendSatisfactionPrompt`
 *                          node.
 *   - `approvedTemplate` — references an approved WhatsApp template by id
 *                          plus the parameter map the materialiser will
 *                          interpolate. Required for WhatsApp deployments
 *                          because the 24h window forces template usage
 *                          for outbound starts.
 *
 * Cross-deployment-type validation (free text rejected on WhatsApp, template
 * rejected on non-WhatsApp) happens at the use-case boundary where the
 * deployment kind is known. This schema only locks the shape.
 */
export const SatisfactionAskCustomerPromptSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("freeText"),
    text: z.string().min(1).max(2000)
  }),
  z.object({
    kind: z.literal("approvedTemplate"),
    templateId: z.string().min(1),
    parameters: z.record(z.string(), z.string()).default({})
  })
]);

export type SatisfactionAskCustomerPrompt = z.infer<typeof SatisfactionAskCustomerPromptSchema>;

/**
 * Operator-facing config for the satisfaction-evaluation preset. Persisted
 * on `Deployment.satisfactionPresetConfig` (per-deployment override) and on
 * `Organization.satisfactionPresetDefaultConfig` (org-wide default the
 * materialiser falls back to when the per-deployment value is null).
 *
 * Snapshotted onto the materialised `Workflow.presetConfig` so the editor's
 * read-only mode and the audit trail can show the exact config that
 * produced the workflow graph.
 *
 * Two evaluation modes — at least one must be enabled or the preset is
 * effectively off and the materialiser refuses to produce a workflow:
 *
 *   - `askCustomer` — sends `askCustomerPrompt` to the customer once
 *                     `idle.thresholdMinutes` has elapsed, then routes the
 *                     reply through `ParseSatisfactionReplyNode`.
 *   - `aiEvaluate`  — runs `AIPassiveSatisfactionScoreNode` on the
 *                     conversation transcript without prompting the customer.
 *                     `aiPrompt` is an operator override of the default
 *                     scoring prompt the node ships with.
 */
export const SatisfactionEvaluationPresetSchema = z
  .object({
    enabled: z.boolean().default(true),
    idle: PlatformListenerIdleConfigSchema,
    modes: z.object({
      askCustomer: z.boolean().default(false),
      aiEvaluate: z.boolean().default(true)
    }),
    askCustomerPrompt: SatisfactionAskCustomerPromptSchema.optional(),
    aiPrompt: z.string().max(8000).optional()
  })
  .superRefine((value, ctx) => {
    if (!value.modes.askCustomer && !value.modes.aiEvaluate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["modes"],
        message: "At least one of modes.askCustomer or modes.aiEvaluate must be enabled."
      });
    }
    if (value.modes.askCustomer && !value.askCustomerPrompt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["askCustomerPrompt"],
        message: "askCustomerPrompt is required when modes.askCustomer is true."
      });
    }
  });

export type SatisfactionEvaluationPreset = z.infer<typeof SatisfactionEvaluationPresetSchema>;

/**
 * Default AI evaluation prompt the materialiser bakes into the
 * AI-passive satisfaction scoring node when the operator doesn't
 * supply a custom prompt. Single source of truth — the backend
 * materialiser AND the frontend draft hook both import this value.
 */
export const DEFAULT_SATISFACTION_AI_PROMPT =
  "Analyse the conversation transcript and rate the customer's satisfaction on the AI passive scale (1 = very negative, 5 = very positive). Cite the most influential signals in your reasoning.";

/**
 * Checks whether an `askCustomerPrompt` shape is deliverable on the given
 * deployment channel. WhatsApp requires approved templates for outbound
 * starts outside the 24h window; every other channel sends plain message
 * bodies. The use-case boundary and the materialiser both call this
 * helper so a config saved through the API or carried in via org-default
 * fallback can't reach the executor with a kind that won't dispatch.
 *
 * Returns null when the prompt is compatible (or absent), an error
 * message string when not. Returning a message instead of a boolean
 * keeps the call sites' error copy in lock-step with the underlying rule.
 */
export function validatePromptKindForDeployment(
  prompt: SatisfactionAskCustomerPrompt | undefined,
  deploymentType: DbEnum.DeploymentType
): string | null {
  if (!prompt) return null;
  const isWhatsApp = deploymentType === DbEnum.DeploymentType.WHATSAPP;
  if (prompt.kind === "approvedTemplate" && !isWhatsApp) {
    return `Approved-template prompts are only deliverable on WhatsApp deployments (received ${deploymentType}). Use a free-text prompt for this channel.`;
  }
  if (prompt.kind === "freeText" && isWhatsApp) {
    return "Free-text prompts are not deliverable on WhatsApp outside the 24-hour customer-care window. Select an approved template instead.";
  }
  return null;
}
